import _ from 'lodash'
import $ from 'jquery'
import moment from 'moment'
import Backbone from 'backbone'
import calendars from 'scoreboard/components/calendars'
import globals from 'scoreboard/components/globals'
import dropdown from 'scoreboard/components/dropdown'
import i18n from 'scoreboard/components/i18n'
import periodReferenceTypeToggle from 'scoreboard/components/period-reference-type-toggle'
import PeriodSelector from 'scoreboard/components/period-selector'
import RelativeOptions from 'scoreboard/components/relative-options'
import template from './calendar-range-selector.hbs'
import selects from 'scoreboard/components/selects'

var byDateVal = 'by-date'
var byRelVal = 'relative'

import Dataset from 'scoreboard/datasets/model/dataset'
const { ALL_TIME } = Dataset

// build-in range adjustment will set the start/end ranges to be equal, callers
// can provide their own adjustments however
function adjustBadRange(first, last, minStart, maxEnd) {
	return [Math.min(Math.max(first, minStart), maxEnd),
		Math.min(Math.max(last, minStart), maxEnd)]
}

function resolveAbsolutePeriod(model) {
	var firstPeriod = calendars.periodsById[model.get('first')]
	var lastPeriod = calendars.periodsById[model.get('last')]

	let badPeriod
	if (model.get('singlePeriod')) {
		// if period doesn't exist, fall back to global period
		badPeriod = !firstPeriod
	} else {
		//if either period doesn't exist, fall back to global period
		badPeriod = !firstPeriod || !lastPeriod
	}

	if (badPeriod) {
		firstPeriod = lastPeriod = calendars.periodsById[globals.periodId]
	}

	if (model.get('singlePeriod')) {
		// if in single period mode, lastPeriod should match firstPeriod
		lastPeriod = firstPeriod
	}

	return {
		calendarId: firstPeriod.calendar.id,
		first: firstPeriod,
		last: lastPeriod
	}
}

var CalendarRangeSelector = Backbone.View.extend({
	className: 'toggler-rows',

	events: {
		'change .calendar select' : '_changeCalendarId',
		'change .relative-selectors select' : '_changeRelativePeriod'
	},

	/**
	 * Allows selection of a single date or calendar period, or a beginning and
	 * ending date or calendar period
	 *
	 * @constructs
	 *
	 * @property {Range} model
	 *
	 * @param {Object} options
	 *
	 * @param {Object} options.range will be converted into this.model, see properties of Range.kt
	 * 				   (calendarId, relative, first, last)
	 * 				   IMPORTANT: even if a single period is being rendered,
	 * 				   the `last` property must be assigned the same value as `first` (23585)
	 *
	 * @param {Boolean} [options.allowAppCalendar=true] If true then the "Current Calendar"
	 * will be an option, and the relative period toggle will be enabled.  If the toggle
	 * is needed and the "Current Calendar" option should be disabled, set allowCurrentCalendar:false
	 *
	 * @param {Boolean} [options.allowCurrentCalendar=allowAppCalendar] whether "Current Calendar" is
	 * an option in the calendar select
	 *
	 * @param {Boolean} [options.singlePeriod=false] Only show one period instead of start/end
	 *
	 * @param {Object} [validRelativeRange={start: -100, end: 100}] range of valid relative periods
	 */
	initialize: function(options) {
		var opts = options || {}

		this._adjustRange = opts.adjustBadRange || adjustBadRange
		this._canChangeCalendar = opts.canChangeCalendar
		this._isFilter = opts.isFilter
		this._allowAllTime = opts.allowAllTime
		// default allowAppCalendar to true, since it was widely used as the default behavior
		this._allowAppCalendar = _.isNil(opts.allowAppCalendar) ? true : opts.allowAppCalendar
		this._allowCurrentCalendar = _.isNil(opts.allowCurrentCalendar) ? this._allowAppCalendar : opts.allowCurrentCalendar
		this._rangeLabels = opts.rangeLabels || {
			singular: 'charts.show',
			plural: 'charts.show'
		}

		if (!this.model) {
			const calendarId = opts.range ? opts.range.calendarId : null
			let range = _.extend(defaultsByCalendarId(calendarId), opts.range)

			range.singlePeriod = opts.singlePeriod || false

			this.model = new Range(range, { allowAppCalendar: this._allowAppCalendar })
			this.model.set('calendarId', calendarId)
		}

		if (this.model.get('relative')) {
			if (!opts.singlePeriod && this.model.get('first') > this.model.get('last')) {
				throw new Error('last must have value greater or equal to first')
			}

			var calendarId = this.model.get('calendarId') || getCalendarId(globals.periodId)
			this.periodSelectors = [
				new PeriodSelector({ calendarId: calendarId, idPrefix: 'from' }),
				new PeriodSelector({ calendarId: calendarId, idPrefix: 'to' })
			]
		} else {
			var range = resolveAbsolutePeriod(this.model)
			if (moment(range.first.end).isAfter(range.last.end, 'day')) {
				throw new Error('last must reference period after first')
			}

			this.periodSelectors = [
				new PeriodSelector({ periodId: range.first.id, idPrefix: 'from' }),
				new PeriodSelector({ periodId: range.last.id, idPrefix: 'to' })
			]
		}

		this.periodSelectors[0].$el.addClass('first')
		this.periodSelectors[1].$el.addClass('last')

		this.listenTo(this.periodSelectors[0], 'period', this.model.first.bind(this.model))
		this.listenTo(this.periodSelectors[1], 'period', this.model.last.bind(this.model))

		this.listenTo(this.model, 'change', function () {
			this._updateInputState()
			this.trigger('change', this.val())
		}.bind(this))

		this.toggle = periodReferenceTypeToggle()

		this.listenTo(this.toggle, 'change', function () {
			this.model.relative(this.toggle.val() == byRelVal)
		}.bind(this))

		let validRelativeRange = _.extend({}, { start: -100, end: 100 },
			opts.validRelativeRange)

		this.relativeOptions = new RelativeOptions(validRelativeRange).generate()
	},

	render: function() {
		var nonEmptyCalendars = _.filter(calendars.calendars, function(cal) {
			return cal.periods && cal.periods.length
		})
		var ctx = {
			calendars: nonEmptyCalendars,
			rangeLabel: this._rangeLabels[this.model.get('singlePeriod') ? 'singular' : 'plural']
		}
		this.$el.html(template(ctx))

		if (!this._canChangeCalendar) {
			this.$('.calendar').addClass('is-not-displayed')
		}

		if (this._allowCurrentCalendar) {
			this.$('.calendar select').prepend(`<option value="">${i18n.get('charts.chart.currentCalendar')}</option>`)
		}

		if (this._allowAllTime) {
			this.$('.calendar select').prepend(`<option value="${ALL_TIME}">${i18n.get('common.allTime')}</option>`)
		}

		//generate and store relative selectors
		this.relativeSelectors = this.$('.relative-selectors select')
		this.relativeSelectors.each(function(i, element) {
			var select = $(element)
			dropdown.idLabels(select, this.relativeOptions, 0)
			select.data('position', i)
		}.bind(this))

		// add absolute period selectors, making sure they are in the DOM
		// before rendering so the label's click/focus can be set up
		this.$('.absolute-selectors .first').after(this.periodSelectors[0].el)
		this.$('.absolute-selectors .last').after(this.periodSelectors[1].el)
		_.invokeMap(this.periodSelectors, 'render')

		//apply selector visibility
		this._updatePeriodSelectorsState()

		//apply absolute type visibilty
		this._updateTypeState()

		// if this is a filter (ex report filters), use 'from' label instead of 'show'
		if (this._isFilter) {
			this.$('.period-range .label .label-text').text(i18n.get('report.customReport.filter.from'))
		}

		this.$('.toggle-container').append(this.toggle.el)
		this.toggle.render()

		this._updateSinglePeriodState(this.model.get('singlePeriod'))

		this._updateInputState()

		return this
	},

	//Returns an object describing the range of the selector using the following
	// * first - the id (or relative distance) of the first period
	// * last - the id (or relative distance) of the last period
	// * relative - true if values are relative references
	// * calendarId - the id of the calendar selected; will be null if "current calendar" selected
	val: function() {
		return this.model.toJSON()
	},

	singlePeriod: function (single) {
		var singlePeriod = !!single

		this._updateSinglePeriodState(singlePeriod)
		this.model.singlePeriod(singlePeriod)
	},

	_updateInputState: function () {
		if (this.model.hasChanged('calendarId')) {
			this._updatePeriodSelectorCalendarState()
		}

		if (this.model.get('calendarId') != ALL_TIME) {
			if (this.model.get('relative')) {
				var first = this.model.get('first')
				var last = this.model.get('last')
				//prevent relative options for being set beyond range
				var start = this.relativeOptions[0].id
				var end = this.relativeOptions[this.relativeOptions.length - 1].id
				var adjustedRange = this._adjustRange(first, last, start, end)
				this.relativeSelectors.eq(0).val(adjustedRange[0])
				this.relativeSelectors.eq(1).val(adjustedRange[1])
				this.model.set({
					'first': adjustedRange[0],
					'last': adjustedRange[1]
				}, {silent: true})
			} else {
				var range = resolveAbsolutePeriod(this.model)
				//if the calendar id is different then the period reference was adjusted
				//because it was bad so adjust the calendar reference as well
				if (this.model.get('calendarId') != range.calendarId) {
					this.model.set('calendarId', range.calendarId, {silent: true})
				}
				this.periodSelectors[0].val(range.first.id)
				this.periodSelectors[1].val(range.last.id)
			}
		}

		// set default calendar
		this.$('.calendar select').val(this.model.get('calendarId') || '')

		this.toggle.val(this.model.get('relative') ? byRelVal : byDateVal, { silent: true })
		this._updateTypeState()
		this._updatePeriodSelectorsState()

		selects(this.el.querySelectorAll('select'))
	},

	_updateSinglePeriodState: function (singlePeriod) {
		this.$('.relative-selectors .last').toggleClass('is-not-displayed', singlePeriod)
		this.$('.absolute-selectors .last').toggleClass('is-not-displayed', singlePeriod)
	},

	_updatePeriodSelectorCalendarState: function () {
		var id = this.model.get('calendarId')

		if (!id || id == ALL_TIME) {
			// if no id ("current calendar") or All Time, and we can't pick a period anyway,
			// so just ignore it
			return
		}

		//set the absolute selectors to the new calendar
		_.invokeMap(this.periodSelectors, 'setCalendar', id)
	},

	_updateTypeState: function() {
		// hide relative/absolute options if set to "current calendar" (ie no calendarId)
		// must be relative in that case
		const calendarId = this.model.get('calendarId')
		const isCurrentCalendar = !!calendarId && calendarId != ALL_TIME
		var isTypeVisible = this._allowAppCalendar && isCurrentCalendar
		this.$el.toggleClass('is-type-visible', !!isTypeVisible)
	},

	_updatePeriodSelectorsState: function() {
		let allTime = this.model.get('calendarId') == ALL_TIME
		this.$('.relative-selectors').toggleClass('is-not-displayed', allTime || !this.model.get('relative'))
		this.$('.absolute-selectors').toggleClass('is-not-displayed', allTime || this.model.get('relative'))
		this.$('.period-range').toggleClass('is-not-displayed', allTime)
	},

	_changeRelativePeriod: function (e) {
		var select = $(e.target)
		var val = parseInt(select.val())

		if (select.hasClass('from')) {
			this.model.first(val)
		} else {
			this.model.last(val)
		}
	},

	_changeCalendarId: function () {
		var id = this.$('.calendar select').val()
		this.model.calendar(id ? parseInt(id) : null)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, this.arguments)
		_.invokeMap(this.periodSelectors, 'remove')
		_.invokeMap([this.toggle], 'remove')
	}
})

function getCalendarId(periodId) {
	return calendars.periodsById[periodId].calendar.id
}

function defaultsByCalendarId (calendarId = null) {
	let def = { calendarId: calendarId }

	if (calendarId != ALL_TIME) {
		if (_.isNil(calendarId)) {
			_.extend(def, {
				relative: true,
				first: 0,
				last: 0
			})
		} else {
			_.extend(def, {
				relative: false,
				first: calendars.correspondingPeriod(calendarId, globals.periodId),
				last: calendars.correspondingPeriod(calendarId, globals.periodId)
			})
		}
	}
	return def
}

var Range = Backbone.Model.extend({
	initialize: function (attr, options) {
		this._allowAppCalendar = options.allowAppCalendar
	},

	first: function (v) {
		this._updatePeriod(true, v)
	},

	last: function (v) {
		this._updatePeriod(false, v)
	},

	_updatePeriod: function (first, v) {
		var relative = this.get('relative')
		var changedAttr = first ? 'first' : 'last'
		var otherAttr = first ? 'last' : 'first'

		var update = {}
		update[changedAttr] = v

		if (this.get('singlePeriod')) {
			update[otherAttr] = v
		} else if (relative) {
			// Make sure the other period is still valid (ie last has to be on or after first)
			if ((first && parseInt(v) > parseInt(this.get(otherAttr)))
					|| (!first && parseInt(v) < parseInt(this.get(otherAttr)))) {
				update[otherAttr] = v
			}
		} else {
			var otherAttrPeriod = calendars.periodsById[this.get(otherAttr)]
			var newPeriod = calendars.periodsById[v]

			if (!otherAttrPeriod || (first && otherAttrPeriod.start.isBefore(newPeriod.start))
					|| (!first && otherAttrPeriod.start.isAfter(newPeriod.start))) {
				update[otherAttr] = v
			}
		}

		this.set(update)
	},

	calendar: function (newId) {
		let oldId = this.get('calendarId')
		if (newId == oldId) {
			return
		}

		// Start out clearing everything out; will take care of resetting what
		// is used later
		var update = {
			calendarId: newId,
			relative: null,
			first: null,
			last: null
		}

		let wasCurrent = !oldId
		let changingToCurrent = !newId

		if (oldId == ALL_TIME) {
			// If we're coming from All Time, we don't have any existing periods/deltas
			// to work with, just use reasonable defaults
			if (changingToCurrent || this._allowAppCalendar) {
				update.relative = true
				update.first = update.last = 0
			} else {
				update.relative = false
				update.first = update.last = calendars.correspondingPeriod(newId, globals.periodId)
			}
		} else if (wasCurrent) {
			// If we're coming from Current, just stay relative and keep the same deltas
			update.relative = true
			update.first = this.get('first')
			update.last = this.get('last')
		} else {
			if (newId == ALL_TIME) {
				// If we're switching to All Time, leave everything cleared out
			} else if (changingToCurrent) {
				update.relative = true

				let wasRelative = this.get('relative')
				if (wasRelative) {
					// If we're switching to Current and we were already relative, just use the same deltas
					update.first = this.get('first')
					update.last = this.get('last')
				} else {
					// If we were absolute, then use deltas that are the same distance apart
					// as the periods that were selected
					update.first = -calendars.distance(this.get('first'), this.get('last'))
					update.last = 0
				}

			} else {
				// We came from a specific calendar (not All Time or Current), and we're
				// switching to a different specific calendar
				update.relative = this.get('relative')

				if (this.get('relative')) {
					// If we were relative, stay relative and use same deltas as before
					update.first = this.get('first')
					update.last = this.get('last')
				} else {
					// If we were absolute, stay absolute and use periods in the new calendar
					// that correspond with the previously selected periods in the old calendar
					update.first = calendars.correspondingPeriod(newId, this.get('first')).id
					update.last = calendars.correspondingPeriod(newId, this.get('last')).id
				}
			}
		}

		this.set(update)
	},

	relative: function (relative) {
		var update = {
			relative: relative
		}

		// #7348 When switching between relative and absolute, we'll use the
		// default period ID for the calendar selected in range selector.
		// This lets us toggle back and forth between absolute and relative
		// without having to worry about the selected calendar being different
		// than global.periodId's calendar
		var defaultPeriodId = calendars.defaultPeriods[this.get('calendarId')]

		if (relative) {
			// was absolute, change to relative periods from the selected calendar's
			// default period
			update.first = calendars.distance(defaultPeriodId, this.get('first'))
			update.last = calendars.distance(defaultPeriodId, this.get('last'))
		} else {
			// was relative, use the same distance for absolute periods, with the
			// selected calendar's default period as a reference point
			update.first = calendars.safeRelativePeriod(defaultPeriodId, this.get('first')).id
			update.last = calendars.safeRelativePeriod(defaultPeriodId, this.get('last')).id
		}

		this.set(update)
	},

	singlePeriod: function (singlePeriod) {
		var update = {
			singlePeriod: singlePeriod
		}

		if (singlePeriod && this.get('first') != this.get('last')) {
			// #17883 when switching to single period, set both to match the later period
			update.first = this.get('last')
		}

		this.set(update)
	}
})

export default CalendarRangeSelector

CalendarRangeSelector.__filename__ = 'components/calendar-range-selector.js'
